import { LoansPaths } from 'app/configs/routesConfig/loans';
import {
  BORROWING_ENTITY_TABS_HREF,
  ENTITY_TYPE_HREF,
} from 'app/pages/loanApplication/tabs/borrowers/EditEntityModal/EditEntityModal.hrefs';
import {
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_VIEWS_PATHS,
} from 'app/pages/loanApplication/tabs/financialInfo/FinancialInfo.constants';
import {
  appFormsTreeAreaNodeMapExtended,
  appFormsTreeFieldOptionsMap,
  AREA_ID_PREFIX,
} from 'app/services/autoLockForms/autoLockForms';
import { LoanEditorSectionNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { normalizeLegacyAppFormsTreeId } from 'app/services/autoLockForms/autoLockForms.utils';
import { DataDisplayTestIds } from 'app/shared/componentsRestyled/coreDesignSystem/DataDisplay/DataDisplay.testIds';
import _ from 'lodash';

import { ToolExecutionStatus } from 'constants/aiChat';
import {
  DocumentsSubtabRouteParam,
  LoanApplicationTabRouteParam,
  TasksSubtabRouteParam,
  UnderwritingSubtabRouteParam,
} from 'constants/loanApplication';
import { getScrollBehavior } from 'utils/motion';

import {
  BORROWER_ENTITY_TYPE_HREF,
  BORROWER_FORM_TAB_TO_SECTION_HREF,
  BORROWERS_SECTION_NAME,
  EDITOR_LINK_TARGETS,
  ENTITY_TYPE_TO_FINANCIAL_VIEW,
  ENTITY_TYPE_TO_TAB,
  FIELD_HIGHLIGHT_CLASS,
  FIELD_HIGHLIGHT_DOM_SETTLE_MS,
  FIELD_HIGHLIGHT_DURATION_MS,
  FIELD_HIGHLIGHT_INTERVAL_MS,
  FIELD_HIGHLIGHT_MAX_ATTEMPTS,
  FIELD_HIGHLIGHT_SCROLL_SETTLE_MS,
  FIELD_TO_EDITOR_LINK_TARGET,
  FINANCIAL_SUBSECTION_TO_VIEW,
  getInPageEditorKey,
  IN_PAGE_EDITORS,
  MAX_WAIT_ATTEMPTS,
  NAV_DIAG_ATTEMPT_LOG_INTERVAL,
  NAV_DIAG_PROBE_SAMPLE_SIZE,
  NAV_DIAG_WINDOW_KEY,
  PROPERTY_AND_LOAN_SUBSECTION_TO_HREF,
  PROPERTY_AND_LOAN_SUBSECTION_TO_TAB_ROUTE,
  PROPERTY_AND_LOAN_TAB_TO_SECTION_HREF,
  SECTION_CONTAINER_ATTR,
  SUBTAB_ROUTES,
  SUPPRESSED_FIELDS_QUERY,
  TAB_NAME_TO_ROUTE,
  VALID_BORROWER_SECTIONS,
  VALID_FINANCIAL_VIEWS,
  VALID_TABS,
  WAIT_INTERVAL_MS,
} from './loanNavigation.constants';
import {
  delay,
  getAreaTabRoute,
  getClosingDrawerRoute,
  getFieldLeaf,
  logNav,
  openInPageEditor,
  resolveTabRoute,
} from './loanNavigation.utils';
import './loanNavigation.css';

const recordDiagnostic = (key, payload) => {
  if (typeof window === 'undefined') {
    return;
  }

  window[NAV_DIAG_WINDOW_KEY] = window[NAV_DIAG_WINDOW_KEY] || {};
  window[NAV_DIAG_WINDOW_KEY][key] = { ...payload, at: new Date().toISOString() };
};

export const navigateToPath = (history, path) => {
  if (history && _.isFunction(history.push)) {
    logNav('navigateToPath:push', { path });
    history.push(path);

    return true;
  }

  logNav('navigateToPath:no-history', { path });

  return false;
};

const findElement = selectorArray =>
  selectorArray.map(selector => document.querySelector(selector)).find(el => el);

const describeElement = element => {
  if (!element) {
    return null;
  }

  return {
    tag: element.tagName,
    id: element.id || null,
    name: element.getAttribute?.('name') || null,
    dataFieldId: element.getAttribute?.('data-field-id') || null,
    classes: element.className || null,
  };
};

const probeSelectors = selectorArray =>
  selectorArray.map(selector => {
    const matches = document.querySelectorAll(selector);

    return {
      selector,
      count: matches.length,
      first: describeElement(matches[0]),
    };
  });

export const waitForElement = (
  selectors,
  maxAttempts = MAX_WAIT_ATTEMPTS,
  intervalMs = WAIT_INTERVAL_MS,
) => {
  const selectorArray = Array.isArray(selectors) ? selectors : [selectors];
  const totalAttempts = maxAttempts;

  const checkElement = async attemptsRemaining => {
    const element = findElement(selectorArray);
    const attempt = totalAttempts - attemptsRemaining + 1;

    if (element) {
      logNav('waitForElement:found', {
        attempt,
        totalAttempts,
        selectorArray,
        element: describeElement(element),
      });

      return element;
    }

    if (attemptsRemaining <= 1) {
      logNav('waitForElement:exhausted', {
        attempt,
        totalAttempts,
        selectorArray,
        probe: probeSelectors(selectorArray),
      });

      return null;
    }

    if (attempt === 1 || attempt % NAV_DIAG_ATTEMPT_LOG_INTERVAL === 0) {
      logNav('waitForElement:attempt', {
        attempt,
        totalAttempts,
        probe: probeSelectors(selectorArray),
      });
    }

    await delay(intervalMs);

    return checkElement(attemptsRemaining - 1);
  };

  return checkElement(maxAttempts);
};

const getFocusableElement = element => {
  if (!element) {
    return null;
  }

  const focusableTags = ['INPUT', 'SELECT', 'TEXTAREA'];

  if (focusableTags.includes(element.tagName)) {
    return element;
  }

  return element.querySelector('input, select, textarea, [tabindex]:not([tabindex="-1"])');
};

const TEXT_INPUT_TYPES = ['text', 'search', 'tel', 'url', 'email', 'password', 'number'];

const focusElementWithCaret = element => {
  if (!element) {
    return;
  }

  element.focus({ preventScroll: true });

  const isTextInput =
    element.tagName === 'TEXTAREA' ||
    (element.tagName === 'INPUT' &&
      TEXT_INPUT_TYPES.includes((element.type || 'text').toLowerCase()));

  if (isTextInput && _.isFunction(element.setSelectionRange)) {
    const valueLength = (element.value || '').length;

    try {
      element.setSelectionRange(valueLength, valueLength);
    } catch {
      // setSelectionRange throws on input types that disallow it (e.g. number on Safari);
      // focus alone is sufficient there.
    }
  }
};

const getHighlightTarget = element => {
  const inputBase = element.closest('.MuiInputBase-root');

  if (inputBase) {
    return inputBase;
  }

  const formGroup = element.closest('.MuiFormGroup-root');

  if (formGroup) {
    return formGroup;
  }

  const radioGroup = element.closest('[role="radiogroup"]');

  if (radioGroup) {
    return radioGroup;
  }

  return element;
};

const getFieldSelectors = (fieldId, formName, fieldLabel = null) => {
  const fieldLeaf = getFieldLeaf(fieldId);

  const editableSelectors = [
    `[data-field-id="${fieldId}"]`,
    `[name="${formName}"] [name="${fieldLeaf}"]`,
    `[id="${fieldId}"]`,
    `[name="${fieldId}"]`,
    `[name="${fieldLeaf}"]`,
    `[data-test="${fieldId}"]`,
    `[name*="${fieldLeaf}"]`,
  ];

  const readOnlySelectors = _.isNil(fieldLabel)
    ? []
    : [`[data-testid="${DataDisplayTestIds.getDataDisplayContentId(fieldLabel)}"]`];

  return [...editableSelectors, ...readOnlySelectors];
};

export const scrollToAndHighlightField = async (fieldId, formName, fieldLabel = null) => {
  const startedAt = performance.now();
  const selectors = getFieldSelectors(fieldId, formName, fieldLabel);

  logNav('scrollToAndHighlightField:start', {
    fieldId,
    formName,
    selectors,
    initialProbe: probeSelectors(selectors),
  });

  const fieldElement = await waitForElement(
    selectors,
    FIELD_HIGHLIGHT_MAX_ATTEMPTS,
    FIELD_HIGHLIGHT_INTERVAL_MS,
  );

  if (!fieldElement) {
    logNav('scrollToAndHighlightField:no-element', {
      fieldId,
      formName,
      selectors,
      elapsedMs: Math.round(performance.now() - startedAt),
      finalProbe: probeSelectors(selectors),
      anyDataFieldIds: Array.from(document.querySelectorAll('[data-field-id]'))
        .slice(0, NAV_DIAG_PROBE_SAMPLE_SIZE)
        .map(el => el.getAttribute('data-field-id')),
    });
    recordDiagnostic('lastFieldHighlight', {
      fieldId,
      formName,
      success: false,
      reason: 'no-element',
      elapsedMs: Math.round(performance.now() - startedAt),
    });

    return;
  }

  await delay(FIELD_HIGHLIGHT_DOM_SETTLE_MS);

  const freshElement = findElement(selectors) || fieldElement;
  const highlightTarget = getHighlightTarget(freshElement);
  const targetRect = highlightTarget.getBoundingClientRect();
  const targetStyle =
    typeof window !== 'undefined' ? window.getComputedStyle(highlightTarget) : null;

  logNav('scrollToAndHighlightField:target-selected', {
    fieldId,
    foundElement: describeElement(freshElement),
    highlightTarget: describeElement(highlightTarget),
    isWrapper: highlightTarget !== freshElement,
    targetRect: {
      top: Math.round(targetRect.top),
      left: Math.round(targetRect.left),
      width: Math.round(targetRect.width),
      height: Math.round(targetRect.height),
    },
    targetVisibility: targetStyle
      ? {
          display: targetStyle.display,
          visibility: targetStyle.visibility,
          opacity: targetStyle.opacity,
        }
      : null,
    elapsedMs: Math.round(performance.now() - startedAt),
  });

  highlightTarget.scrollIntoView({ behavior: getScrollBehavior(), block: 'center' });

  await delay(FIELD_HIGHLIGHT_SCROLL_SETTLE_MS);

  const focusable = getFocusableElement(freshElement);

  focusElementWithCaret(focusable);

  highlightTarget.classList.add(FIELD_HIGHLIGHT_CLASS);

  const postScrollRect = highlightTarget.getBoundingClientRect();

  logNav('scrollToAndHighlightField:applied', {
    fieldId,
    focusableTag: focusable?.tagName || null,
    focusableType: focusable?.type || null,
    activeElementMatches: typeof document !== 'undefined' && document.activeElement === focusable,
    classApplied: highlightTarget.classList.contains(FIELD_HIGHLIGHT_CLASS),
    durationMs: FIELD_HIGHLIGHT_DURATION_MS,
    postScrollRect: {
      top: Math.round(postScrollRect.top),
      left: Math.round(postScrollRect.left),
      width: Math.round(postScrollRect.width),
      height: Math.round(postScrollRect.height),
    },
    elapsedMs: Math.round(performance.now() - startedAt),
  });
  recordDiagnostic('lastFieldHighlight', {
    fieldId,
    formName,
    success: true,
    elapsedMs: Math.round(performance.now() - startedAt),
    target: describeElement(highlightTarget),
  });

  delay(FIELD_HIGHLIGHT_DURATION_MS).then(() => {
    highlightTarget.classList.remove(FIELD_HIGHLIGHT_CLASS);
    logNav('scrollToAndHighlightField:cleared', { fieldId });
  });
};

const AREA_ID_PREFIXES = Object.values(AREA_ID_PREFIX);

/**
 * Navigate to whatever a checklist item's `fieldRef` points at (DEV-5218): an AREA
 * ref (`page:`/`section:`/`subsection:`/`tab:` — authored by the section-level
 * field link, DEV-4467) routes to the section; a field path routes to the field.
 * The single entry every "Go to field" affordance should use — passing an area ref
 * into `handleNavigateToField` fails its field-map lookup and silently no-ops.
 */
export const navigateToFieldRef = ({ parameters, context }) => {
  const { borrowerId } = parameters;
  // Links saved before LEN2-499 may carry the nameless Lead/Activities/Tasks ids.
  const fieldRef = parameters.fieldRef && normalizeLegacyAppFormsTreeId(parameters.fieldRef);

  if (AREA_ID_PREFIXES.some(prefix => fieldRef?.startsWith(prefix))) {
    return navigateToSection({ parameters: { areaId: fieldRef }, context });
  }

  return handleNavigateToField({ parameters: { fieldId: fieldRef, borrowerId }, context });
};

/** Path to a checklist's Alerts subtab — the base every alerts deep link builds on. */
export const generateAlertsSubtabPath = ({ applicationId, checklistId }) =>
  LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: TAB_NAME_TO_ROUTE.alerts,
    subtab: checklistId,
  });

/**
 * Deep link to the Suppressed fields section on a checklist's Alerts subtab
 * (DEV-5146): `?view=suppressed` expands + scrolls to the section, `&item=` also
 * highlights that row. Shared by the nav badge and the suppression toast so both
 * entry points land identically.
 */
export const generateSuppressedFieldsPath = ({ applicationId, checklistId, itemId }) => {
  const path = generateAlertsSubtabPath({ applicationId, checklistId });

  const query = new URLSearchParams({
    [SUPPRESSED_FIELDS_QUERY.VIEW_KEY]: SUPPRESSED_FIELDS_QUERY.VIEW_VALUE,
    ...(itemId ? { [SUPPRESSED_FIELDS_QUERY.ITEM_KEY]: itemId } : {}),
  });

  return `${path}?${query.toString()}`;
};

export const handleNavigateToTab = ({ parameters, context }) => {
  const { history, applicationId } = context;
  const { tabName } = parameters;

  if (!tabName) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Tab name is required',
    };
  }

  const normalizedTabName = _.kebabCase(tabName);

  if (!VALID_TABS.includes(normalizedTabName)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid tab name: ${tabName}. Valid tabs are: ${VALID_TABS.join(', ')}`,
    };
  }

  const tabRoute = resolveTabRoute(
    TAB_NAME_TO_ROUTE[normalizedTabName] || normalizedTabName,
    context,
  );

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: tabRoute,
  });

  if (!navigateToPath(history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: { navigatedTo: tabRoute },
  };
};

export const handleNavigateToSubtab = ({ parameters, context }) => {
  const { history, applicationId } = context;
  const { tabName, subtabName } = parameters;

  if (!tabName || !subtabName) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Tab name and subtab name are required',
    };
  }

  const normalizedTabName = _.kebabCase(tabName);
  const normalizedSubtabName = _.kebabCase(subtabName);

  if (!VALID_TABS.includes(normalizedTabName)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid tab name: ${tabName}. Valid tabs are: ${VALID_TABS.join(', ')}`,
    };
  }

  const subtabRoutes = SUBTAB_ROUTES[normalizedTabName];

  if (!subtabRoutes) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Tab "${tabName}" does not have subtabs`,
    };
  }

  const validSubtabs = Object.values(subtabRoutes);
  const subtabRoute =
    subtabRoutes[normalizedSubtabName.toUpperCase().replace(/-/g, '_')] || normalizedSubtabName;

  if (!validSubtabs.includes(subtabRoute) && !validSubtabs.includes(normalizedSubtabName)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid subtab "${subtabName}" for tab "${tabName}". Valid subtabs are: ${validSubtabs.join(
        ', ',
      )}`,
    };
  }

  const tabRoute = resolveTabRoute(
    TAB_NAME_TO_ROUTE[normalizedTabName] || normalizedTabName,
    context,
  );

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: tabRoute,
    subtab: subtabRoute,
  });

  if (!navigateToPath(history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: { navigatedTo: `${tabRoute}/${subtabRoute}` },
  };
};

export const handleNavigateToBorrower = ({ parameters, context }) => {
  const { history, applicationId } = context;
  const { borrowerId, section } = parameters;

  if (!borrowerId) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Borrower ID is required',
    };
  }

  const normalizedSection = section ? _.kebabCase(section) : BORROWING_ENTITY_TABS_HREF.BORROWER;

  if (section && !VALID_BORROWER_SECTIONS.includes(normalizedSection)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid borrower section: ${section}. Valid sections are: ${VALID_BORROWER_SECTIONS.join(
        ', ',
      )}`,
    };
  }

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: LoanApplicationTabRouteParam.BORROWERS,
    subtab: ENTITY_TYPE_HREF.BORROWER,
    entityId: borrowerId,
    section: normalizedSection,
  });

  if (!navigateToPath(history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: {
      navigatedTo: path,
      borrowerId,
      section: normalizedSection,
    },
  };
};

export const handleNavigateToFinancialView = ({ parameters, context }) => {
  const { history, applicationId } = context;
  const { viewName, borrowerId } = parameters;

  if (!viewName) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'View name is required',
    };
  }

  const normalizedViewName = _.kebabCase(viewName);

  if (!VALID_FINANCIAL_VIEWS.includes(normalizedViewName)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid financial view: ${viewName}. Valid views are: ${VALID_FINANCIAL_VIEWS.join(
        ', ',
      )}`,
    };
  }

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: LoanApplicationTabRouteParam.FINANCIAL_INFO,
    subtab: normalizedViewName,
    entityId: borrowerId || ALL_BORROWERS_PATH,
  });

  if (!navigateToPath(history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: {
      navigatedTo: path,
      viewName: normalizedViewName,
      borrowerId: borrowerId || 'all',
    },
  };
};

const scrollToSection = async areaId => {
  const sectionElement = await waitForElement(`[${SECTION_CONTAINER_ATTR}="${areaId}"]`);

  if (!sectionElement) {
    logNav('scrollToSection:no-element', { areaId });

    return;
  }

  sectionElement.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
  sectionElement.classList.add(FIELD_HIGHLIGHT_CLASS);

  delay(FIELD_HIGHLIGHT_DURATION_MS).then(() => {
    sectionElement.classList.remove(FIELD_HIGHLIGHT_CLASS);
    logNav('scrollToSection:cleared', { areaId });
  });
};

// Financial-info areas and fields both live inside a financial VIEW (not a subtab
// route of their own) — shared by navigateToSection and handleNavigateToField so
// the view routing cannot drift between them (DEV-5218).
const navigateToFinancialViewForSubsection = ({ subsection, context, onSuccess }) => {
  const viewName = FINANCIAL_SUBSECTION_TO_VIEW[subsection] || FINANCIAL_INFO_VIEWS_PATHS.ALL_VIEWS;
  const result = handleNavigateToFinancialView({ parameters: { viewName }, context });

  if (result.success) {
    onSuccess();
  }

  return result;
};

// An editor screen with no appFormsTree area (e.g. Programs & Pricing) — open its
// drawer straight on it rather than scrolling a page section (LEN2-499).
const navigateToEditorLinkTarget = ({ areaId, editorLinkTarget, context }) => {
  const { tab, subtab, entityId } = editorLinkTarget;

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId: context.applicationId,
    tab,
    subtab,
    entityId,
  });

  if (!navigateToPath(context.history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: { navigatedTo: path, areaId },
  };
};

/**
 * Navigates to a loan-editor SECTION/area (no field focus required) and scrolls
 * its container into view — the section-level counterpart to handleNavigateToField
 * (DEV-4467). Reuses the existing financial-view / subtab routing so a linked
 * section (e.g. Employment & Income) opens in its dedicated view.
 */
export const navigateToSection = ({ parameters, context }) => {
  const { areaId } = parameters;

  if (!areaId) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Area ID is required',
    };
  }

  const editorLinkTarget = EDITOR_LINK_TARGETS[areaId];

  if (editorLinkTarget) {
    return navigateToEditorLinkTarget({ areaId, editorLinkTarget, context });
  }

  const areaNode = appFormsTreeAreaNodeMapExtended[areaId];

  if (!areaNode) {
    logNav('navigateToSection:unknown-areaId', { areaId });

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid area ID: ${areaId}. Section not found in the field definitions.`,
    };
  }

  const { section, subsection } = areaNode;

  if (section === LoanEditorSectionNames.FINANCIAL_INFO) {
    return navigateToFinancialViewForSubsection({
      subsection,
      context,
      onSuccess: () => scrollToSection(areaId),
    });
  }

  const tabRoute = getAreaTabRoute({ section, subsection, context });

  if (!tabRoute) {
    logNav('navigateToSection:no-tab-route', { areaId, section });

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Cannot navigate to section: ${areaId}`,
    };
  }

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId: context.applicationId,
    tab: tabRoute,
  });

  if (!navigateToPath(context.history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  scrollToSection(areaId);

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: { navigatedTo: path, areaId, section },
  };
};

const navigateToTask = async ({ applicationId, entityId, history, tabRoute }) => {
  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: tabRoute,
    subtab: TasksSubtabRouteParam.PENDING,
  });

  navigateToPath(history, path);

  // The Tasks table marks each row with `data-row-id` (no per-task test id) and opens the
  // task's info dialog on a row click (LEN2-499).
  await openEntityRowById(entityId);

  return path;
};

const navigateToCondition = async ({ applicationId, entityId, history, tabRoute }) => {
  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: tabRoute,
    subtab: UnderwritingSubtabRouteParam.CONDITIONS,
  });

  navigateToPath(history, path);

  const conditionRow = await waitForElement(`[data-testid="${entityId}"]`);

  if (conditionRow) {
    conditionRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    conditionRow.classList.add(FIELD_HIGHLIGHT_CLASS);
    delay(FIELD_HIGHLIGHT_DURATION_MS).then(() => {
      conditionRow.classList.remove(FIELD_HIGHLIGHT_CLASS);
    });
  }

  return path;
};

const navigateToDocument = async ({ applicationId, entityId, history, tabRoute }) => {
  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: tabRoute,
    subtab: DocumentsSubtabRouteParam.DOCUMENTS,
  });

  navigateToPath(history, path);

  const documentElement = await waitForElement(`[data-document-id="${entityId}"]`);

  if (documentElement) {
    documentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    documentElement.click();
  }

  return path;
};

const navigateToFinancialEntity = async ({
  applicationId,
  entityId,
  history,
  tabRoute,
  entityType,
}) => {
  const financialView = ENTITY_TYPE_TO_FINANCIAL_VIEW[entityType];

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: tabRoute,
    subtab: financialView,
    entityId: ALL_BORROWERS_PATH,
  });

  navigateToPath(history, path);

  await openEntityRowById(entityId);

  return path;
};

export const openEntityRowById = async entityId => {
  if (_.isNil(entityId)) {
    return false;
  }

  const entityRow = await waitForElement([
    `[data-row-id="${entityId}"]`,
    `[data-entity-id="${entityId}"]`,
    `[data-testid="${entityId}"]`,
  ]);

  if (!entityRow) {
    return false;
  }

  entityRow.scrollIntoView({ behavior: getScrollBehavior(), block: 'center' });
  entityRow.click();

  return true;
};

const ENTITY_NAVIGATION_HANDLERS = {
  task: navigateToTask,
  condition: navigateToCondition,
  document: navigateToDocument,
  income: navigateToFinancialEntity,
  asset: navigateToFinancialEntity,
  liability: navigateToFinancialEntity,
  reo: navigateToFinancialEntity,
};

export const handleNavigateToEntity = ({ parameters, context }) => {
  const { history, applicationId } = context;
  const { entityType, entityId } = parameters;

  if (!entityType || !entityId) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Entity type and entity ID are required',
    };
  }

  const normalizedEntityType = entityType.toLowerCase();
  const targetTab = ENTITY_TYPE_TO_TAB[normalizedEntityType];

  if (!targetTab) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid entity type: ${entityType}. Valid types are: ${Object.keys(
        ENTITY_TYPE_TO_TAB,
      ).join(', ')}`,
    };
  }

  const tabRoute = resolveTabRoute(TAB_NAME_TO_ROUTE[targetTab], context);
  const handler = ENTITY_NAVIGATION_HANDLERS[normalizedEntityType];

  const path = handler({
    applicationId,
    entityId,
    history,
    tabRoute,
    entityType: normalizedEntityType,
  });

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: {
      navigatedTo: path,
      entityType: normalizedEntityType,
      entityId,
    },
  };
};

const navigateToInPageEditorField = ({ fieldId, fieldOption, inPageEditor, context }) => {
  const { subtab, openers = [] } = inPageEditor;
  const tabRoute = resolveTabRoute(inPageEditor.tabRoute, context);

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId: context.applicationId,
    tab: tabRoute,
    subtab,
  });

  if (!navigateToPath(context.history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  openInPageEditor(openers).then(() =>
    scrollToAndHighlightField(fieldId, fieldOption.form, fieldOption.name),
  );

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: {
      navigatedTo: path,
      fieldId,
      subsection: fieldOption.subsection,
      tab: fieldOption.tab,
    },
  };
};

const buildEditorFieldPath = ({ applicationId, tabRoute, section, subsection, tab, fieldId }) => {
  const closingDrawerRoute =
    section === LoanEditorSectionNames.CLOSING
      ? getClosingDrawerRoute({ subsection, tab, fieldId })
      : null;

  if (!_.isNil(closingDrawerRoute)) {
    return LoansPaths.applicationManagement.generatePath({
      applicationId,
      tab: closingDrawerRoute.tabRoute,
      subtab: closingDrawerRoute.subtab,
    });
  }

  return LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: PROPERTY_AND_LOAN_SUBSECTION_TO_TAB_ROUTE[subsection] ?? tabRoute,
    // The tree names are camelCase but the edit-modal routes match kebab hrefs —
    // an unmapped raw name changes the URL without opening the modal (DEV-5218).
    subtab: PROPERTY_AND_LOAN_SUBSECTION_TO_HREF[subsection] ?? subsection,
    section: PROPERTY_AND_LOAN_TAB_TO_SECTION_HREF[tab] ?? tab,
  });
};

export const handleNavigateToField = ({ parameters, context }) => {
  const { history, applicationId } = context;
  const { fieldId, borrowerId } = parameters;

  logNav('handleNavigateToField:enter', {
    fieldId,
    borrowerId: borrowerId || null,
    applicationId,
    hasHistory: !!history,
  });

  if (!fieldId) {
    logNav('handleNavigateToField:missing-fieldId', {});

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Field ID is required',
    };
  }

  const fieldOption = appFormsTreeFieldOptionsMap[fieldId];

  if (!fieldOption) {
    logNav('handleNavigateToField:unknown-fieldId', {
      fieldId,
      knownFieldIdsCount: Object.keys(appFormsTreeFieldOptionsMap).length,
    });

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Invalid field ID: ${fieldId}. Field not found in the field definitions.`,
    };
  }

  const { section, subsection, tab, form } = fieldOption;

  logNav('handleNavigateToField:resolved-field', {
    fieldId,
    fieldOption: { section, subsection, tab, form },
  });

  const editorLinkTarget = FIELD_TO_EDITOR_LINK_TARGET[fieldId];

  if (editorLinkTarget) {
    return navigateToEditorLinkTarget({ areaId: fieldId, editorLinkTarget, context });
  }

  const inPageEditor = IN_PAGE_EDITORS[getInPageEditorKey(subsection, tab)];

  if (inPageEditor) {
    return navigateToInPageEditorField({ fieldId, fieldOption, inPageEditor, context });
  }

  const tabRoute = getAreaTabRoute({ section, subsection, context });

  if (!tabRoute) {
    logNav('handleNavigateToField:no-tab-route', { section, subsection });

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Cannot navigate to field in section: ${section ?? fieldOption.sectionLabel}`,
    };
  }

  if (section === LoanEditorSectionNames.FINANCIAL_INFO) {
    return navigateToFinancialViewForSubsection({
      subsection,
      context,
      onSuccess: () => scrollToAndHighlightField(fieldId, form, fieldOption.name),
    });
  }

  const isBorrowerScopedField = section === BORROWERS_SECTION_NAME;
  const scopedEntityId = isBorrowerScopedField && borrowerId ? borrowerId : null;
  const borrowerSectionHref = isBorrowerScopedField ? BORROWER_FORM_TAB_TO_SECTION_HREF[tab] : null;

  if (isBorrowerScopedField && !scopedEntityId) {
    logNav('handleNavigateToField:missing-borrowerId', { fieldId, tab });

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'borrowerId is required to navigate to a borrower-scoped field',
    };
  }

  if (isBorrowerScopedField && !borrowerSectionHref) {
    logNav('handleNavigateToField:unknown-borrower-form-tab', { fieldId, tab });

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: `Cannot resolve borrower drawer section for form tab: ${tab}`,
    };
  }

  const path = isBorrowerScopedField
    ? LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab: tabRoute,
        subtab: BORROWER_ENTITY_TYPE_HREF,
        entityId: scopedEntityId,
        section: borrowerSectionHref,
      })
    : buildEditorFieldPath({
        applicationId,
        tabRoute,
        section,
        subsection,
        tab,
        fieldId,
      });

  logNav('handleNavigateToField:built-path', {
    fieldId,
    path,
    isBorrowerScopedField,
    scopedEntityId,
    borrowerSectionHref,
    currentPathname: typeof window !== 'undefined' ? window.location?.pathname : null,
  });

  if (!navigateToPath(history, path)) {
    logNav('handleNavigateToField:nav-failed', { path });

    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  scrollToAndHighlightField(fieldId, form, fieldOption.name);

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: {
      navigatedTo: path,
      fieldId,
      section,
      subsection,
      tab,
      form,
    },
  };
};

export const navigationToolHandler = async ({ toolName, parameters, context }) => {
  const { history, applicationId } = context;

  if (!history) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  if (!applicationId) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Application ID is required for navigation',
    };
  }

  try {
    switch (toolName) {
      case 'navigate_to_tab':
        return handleNavigateToTab({ parameters, context });

      case 'navigate_to_subtab':
        return handleNavigateToSubtab({ parameters, context });

      case 'navigate_to_borrower':
        return handleNavigateToBorrower({ parameters, context });

      case 'navigate_to_financial_view':
        return handleNavigateToFinancialView({ parameters, context });

      case 'navigate_to_entity':
        return handleNavigateToEntity({ parameters, context });

      case 'navigate_to_field':
        return handleNavigateToField({ parameters, context });

      default:
        return {
          success: false,
          status: ToolExecutionStatus.FAILED,
          error: `Unknown navigation tool: ${toolName}`,
        };
    }
  } catch (error) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: error.message || 'Navigation failed',
    };
  }
};

export default navigationToolHandler;
